import { Entries } from 'superlikers-microsites-api'
import {
  CircuitKpisSummary,
  ExternalCircuitKpisSummary,
  ExternalKpi,
  KpiDetails,
  ExternalKpiSummary,
  Kpi,
  KpisSummary
} from '../interfaces/kpis.interface'

/**
 * Adapta los datos de KPI externos al modelo interno de la aplicación
 *
 * @param {string} uid - Identificador único del usuario
 * @param {Entries<ExternalKpi>} external - Datos externos del KPI
 * @returns {Kpi} Objeto KPI adaptado con datos transformados
 */
export const adapterKpi = (uid: string, external: Entries<ExternalKpi>): Kpi => {
  const { data, slug, category } = external

  const kpi = {
    slug,
    name: data?.name,
    category,
    month: category.replace('kpi_', ''),
    goal: transformStringToNumber(data?.goal),
    progress: transformStringToNumber(data?.progress),
    percentage: transformStringToNumber(data?.percentage) * 100,
    userUid: uid,
    show: true,
    help_text: data?.help_text
  } as Kpi

  if (data?.show_details) kpi.show_details = data.show_details === 'VERDADERO'
  if (data?.details_name) kpi.details_name = data?.details_name
  if (data?.details_format) kpi.details_format = data?.details_format

  return kpi
}

export const adapterKpiSummary = (uid: string, external: Entries<ExternalKpiSummary>) => {
  const { data, category } = external

  const percentage = transformStringToNumber(data?.percentage) * 100
  const month = category.replace('summary-', '')

  const kpiSummary = {
    percentage,
    userUid: uid,
    month,
    category
  } as KpisSummary

  return kpiSummary
}

export const adapterCircuitKpiSummary = (uid: string, external: Entries<ExternalCircuitKpisSummary>) => {
  const { data, category } = external

  const percentage = transformStringToNumber(data?.percentage) * 100
  const points = transformStringToNumber(data?.points)
  const pointsName = data?.points_name

  const [, circuit, month] = category.split('-')

  const kpiSummary = {
    percentage,
    userUid: uid,
    circuit,
    points,
    month,
    category,
    points_name: pointsName,
    avance: data?.avance,
    meta: data?.meta
  } as CircuitKpisSummary

  return kpiSummary
}

export const adapterKpiCircuit = (uid: string, external: Entries<ExternalKpi>, details: Entries<KpiDetails>[]): Kpi => {
  const { data, slug, category } = external
  const points = data?.win_points
  const pointsName = data?.points_name

  const [circuit, month] = category.split('-')

  // para evitar que se repitan los detalles se utiliza un map usando el kpi y el title como key
  const uniqueDetailsMap = new Map<string, KpiDetails>()

  details.forEach(detail => {
    const key = `${detail.data?.kpi ?? ''}-${detail.data?.title ?? ''}`
    if (!uniqueDetailsMap.has(key)) {
      const kpiDetail = {
        title: detail.data?.title,
        description: detail.data?.description,
        kpi: detail.data?.kpi
      } as KpiDetails

      uniqueDetailsMap.set(key, kpiDetail)
    }
  })

  const kpiDetails = Array.from(uniqueDetailsMap.values())

  const kpi = {
    slug,
    name: data?.name,
    category,
    goal: transformStringToNumber(data?.goal),
    progress: transformStringToNumber(data?.progress),
    percentage: transformStringToNumber(data?.percentage) * 100,
    userUid: uid,
    month,
    circuit,
    show: false,
    details: kpiDetails
  } as Kpi

  // solo aplica para kpis que vienen con los circuitos
  if (points) kpi.win_points = transformStringToNumber(points)
  if (pointsName) kpi.points_name = pointsName

  if (data?.show_details) kpi.show_details = data.show_details === 'VERDADERO'
  if (data?.details_name) kpi.details_name = data?.details_name
  if (data?.details_format) kpi.details_format = data?.details_format

  return kpi
}

/**
 * Transforma un string a un número con un número específico de decimales
 *
 * @param {string} [str] - String a convertir en número
 * @param {number} decimals - Número de decimales a mantener (por defecto: 2)
 * @returns {number} El número convertido con los decimales especificados o 0 si la conversión falla
 */
export const transformStringToNumber = (str?: string, decimals = 2): number => {
  if (!str || isNaN(Number(str))) return 0

  return Number(Number(str).toFixed(decimals))
}
